import dayjs from "dayjs";
import { useEffect, useMemo, useState, Fragment } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Combobox } from "@/components/Combobox";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Receipt, Loader2, Download, Settings2, Plus, Trash2, FileWarning, User, Users, ChevronDown, ChevronRight, History, RotateCcw, UploadCloud } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { XeroSettings } from "@/components/XeroSettings";
import { InvoiceCheckPanel } from "@/components/InvoiceCheckPanel";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { fmtLog } from "@/lib/tz";
import { Switch } from "@/components/ui/switch";
import { orgToday, orgNowLocal } from "@/lib/tz";

const MGMT = ["NDIA Managed", "Plan Managed", "Self Managed", "Dual (NDIA + Plan Manager)", "Private"];
const DEST_COLOUR = { NDIA: "bg-blue-50 text-blue-700 border-blue-200", "Plan Manager": "bg-purple-50 text-purple-700 border-purple-200", Self: "bg-amber-50 text-amber-700 border-amber-200" };
const money = (n) => `$${Number(n || 0).toFixed(2)}`;
// A short key that changes whenever any line that would be sent changes, so the
// invoice check panel starts again (the server refuses lines it didn't check).
const linesKey = (lines) => {
  const raw = (lines || []).map((l) => [l.participant, l.destination, l.shift, l.shift_date, l.support_number,
    l.from, l.to, l.is_unit ? l.quantity : l.hours, l.unit_price].join("|")).join(";");
  let h = 0;
  for (let i = 0; i < raw.length; i += 1) h = (h * 31 + raw.charCodeAt(i)) | 0;
  return `${(lines || []).length}-${h}`;
};
// NDIA portal rejects file names (incl. extension) longer than 20 chars.
const shortCsvName = () => {
  return `PR${orgNowLocal().format("YYMMDDHHmmss")}.csv`;
};

export default function Invoicing() {
  const { can } = useAuth();
  const [mode, setMode] = useState("single"); // single | bulk | setup
  const [participants, setParticipants] = useState([]);
  const [groups, setGroups] = useState([]);
  const [participant, setParticipant] = useState(null);
  const todayIso = orgToday(); // org timezone, not the browser clock
  const minus90 = (iso) => dayjs(iso).subtract(90, "day").format("YYYY-MM-DD");
  const [to, setTo] = useState(todayIso);
  const [from, setFrom] = useState(minus90(todayIso));
  const [customRange, setCustomRange] = useState(false);

  // Auto-compute 90-day lookback whenever "Bill To" changes, unless custom range is enabled.
  useEffect(() => {
    if (!customRange) setFrom(minus90(to));
  }, [to, customRange]);
  const [bill, setBill] = useState(null);       // participant billing config
  const [preview, setPreview] = useState(null); // single preview result
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showSetup, setShowSetup] = useState(false);

  // bulk state
  const [bulk, setBulk] = useState(null);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [showLines, setShowLines] = useState(false);
  const [excluded, setExcluded] = useState(() => new Set()); // participant names held back from this run

  // invoice run history
  const [runs, setRuns] = useState([]);
  const [runsOpen, setRunsOpen] = useState(false);
  const [expandedRun, setExpandedRun] = useState(null);
  const [voidTarget, setVoidTarget] = useState(null);
  const [voidReason, setVoidReason] = useState("");
  const [voidPart, setVoidPart] = useState(null);   // { run, participant, display }
  const [voidPartReason, setVoidPartReason] = useState("");

  const loadRuns = () => api.invoiceRuns().then(setRuns).catch(() => {});
  // After a lock, jump the user straight to that run in the history so they can
  // grab its NDIA CSV.
  const goToRun = async (runName) => {
    await loadRuns();
    setRunsOpen(true);
    if (!runName) return;
    try { setExpandedRun(await api.invoiceRun(runName)); } catch {}
    setTimeout(() => document.querySelector(`[data-testid="run-${runName}"]`)
      ?.scrollIntoView({ behavior: "smooth", block: "center" }), 350);
  };
  const [holdbacks, setHoldbacks] = useState([]);
  const loadHoldbacks = () => api.invoiceHoldbacks().then(setHoldbacks).catch(() => setHoldbacks([]));

  // Xero
  const [xero, setXero] = useState({ connected: false, tenant_name: "", account_code_set: false });
  const [showXero, setShowXero] = useState(false);
  const [pushed, setPushed] = useState({}); // reference -> bool (already pushed to Xero)
  const loadXero = () => api.xeroStatus().then(setXero).catch(() => {});
  const singleRef = participant ? `INV-${participant}-${from}` : "";
  const bulkRef = `INV-BULK-${from}-${to}`;
  const [singleCheck, setSingleCheck] = useState(null);   // id of a passed invoice check
  const [bulkCheck, setBulkCheck] = useState(null);
  const checkPushed = (ref) => { if (!ref) return; api.xeroPushStatus(ref).then((s) => setPushed((p) => ({ ...p, [ref]: s.pushed }))).catch(() => {}); };

  const loadFromHoldback = async (hb) => {
    setMode("single");
    setCustomRange(true); // preserve the holdback's exact date range
    if (hb.date_from) setFrom(hb.date_from);
    if (hb.date_to) setTo(hb.date_to);
    await loadBilling(hb.participant);
    try {
      const pv = await api.invoicingPreview({ participant: hb.participant, date_from: hb.date_from, date_to: hb.date_to });
      setPreview(pv); setBill(pv.participant);
      checkPushed(`INV-${hb.participant}-${hb.date_from}`);
      toast.info(`Loaded ${hb.participant_name} (${hb.date_from} → ${hb.date_to}) — edit lines then generate their invoice`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not load held-back participant"); }
  };

  const dismissHoldback = async (id) => {
    try { await api.dismissHoldback(id); loadHoldbacks(); toast.success("Removed from held-back list"); }
    catch { toast.error("Could not remove"); }
  };

  useEffect(() => {
    api.participants().then(setParticipants).catch(() => {});
    api.itemGroups().then(setGroups).catch(() => {});
    loadRuns();
    loadHoldbacks();
    loadXero();
    const q = new URLSearchParams(window.location.search).get("xero");
    if (q) {
      const msg = { connected: "Connected to Xero ✓", error: "Xero authorisation was declined", badstate: "Xero session expired — try again", tokenfail: "Xero token exchange failed" };
      q === "connected" ? toast.success(msg[q]) : toast.error(msg[q] || "Xero connection issue");
      window.history.replaceState({}, "", window.location.pathname);
      loadXero();
    }
  }, []);

  const pOpts = useMemo(() => participants.map((p) => ({ value: p.name, label: p.display_name || p.name })), [participants]);
  const groupOpts = useMemo(() => (groups || []).map((g) => (typeof g === "string" ? g : g.name)), [groups]);

  const loadBilling = async (name) => {
    setParticipant(name); setPreview(null); setBill(null);
    if (!name) return;
    try { setBill(await api.participantBilling(name)); } catch { toast.error("Could not load participant billing"); }
  };

  const runPreview = async () => {
    if (!participant) return toast.error("Pick a participant");
    setLoading(true);
    try {
      const pv = await api.invoicingPreview({ participant, date_from: from, date_to: to });
      setPreview(pv); setBill(pv.participant);
      checkPushed(`INV-${participant}-${from}`);
      if (pv.lines.length === 0) toast.info(`No billable approved shifts in range${pv.warnings?.length ? " (see warnings)" : ""}.`);
    } catch (e) { toast.error(e.response?.data?.detail || "Preview failed"); }
    finally { setLoading(false); }
  };

  const runBulkPreview = async () => {
    setBulkLoading(true); setBulk(null); setExcluded(new Set());
    try {
      const bv = await api.invoicingBulkPreview({ date_from: from, date_to: to });
      setBulk(bv);
      checkPushed(`INV-BULK-${from}-${to}`);
      if (!bv.lines.length) toast.info(`No billable approved shifts across any participant in range${bv.warnings?.length ? " (see warnings)" : ""}.`);
    } catch (e) { toast.error(e.response?.data?.detail || "Bulk preview failed"); }
    finally { setBulkLoading(false); }
  };

  const toggleExclude = (name) => setExcluded((prev) => {
    const next = new Set(prev);
    next.has(name) ? next.delete(name) : next.add(name);
    return next;
  });

  const saveBilling = async () => {
    setBusy(true);
    try {
      const saved = await api.saveParticipantBilling(participant, {
        management_type: bill.management_type,
        plan_manager_name: bill.plan_manager_name, plan_manager_email: bill.plan_manager_email,
        plan_manager_abn: bill.plan_manager_abn, self_bill_name: bill.self_bill_name,
        self_bill_email: bill.self_bill_email,
        category_billing: bill.category_billing || [],
        two_to_one_lines: bill.two_to_one_lines || "separate",
        complex_support_needs: !!bill.complex_support_needs,
      });
      setBill(saved); toast.success("Billing setup saved"); setShowSetup(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const setLine = (idx, key, val) => setPreview((pv) => {
    const lines = pv.lines.map((l, i) => (i === idx ? { ...l, [key]: val } : l));
    const totals = { NDIA: 0, "Plan Manager": 0, Self: 0 };
    lines.forEach((l) => { totals[l.destination] += l.amount; });
    return { ...pv, lines, totals: Object.fromEntries(Object.entries(totals).map(([k, v]) => [k, Number(v.toFixed(2))])) };
  });

  // Manual edit of a numeric line field (hours / quantity / unit price) — recomputes
  // that line's amount and all destination totals + the grand total.
  const setLineNum = (idx, key, val) => setPreview((pv) => {
    const lines = pv.lines.map((l, i) => {
      if (i !== idx) return l;
      const nl = { ...l, [key]: val };
      const qty = nl.is_unit ? (Number(nl.quantity) || 0) : (Number(nl.hours) || 0);
      nl.amount = Number((qty * (Number(nl.unit_price) || 0)).toFixed(2));
      return nl;
    });
    const totals = { NDIA: 0, "Plan Manager": 0, Self: 0 };
    lines.forEach((l) => { totals[l.destination] += l.amount; });
    return {
      ...pv, lines,
      totals: Object.fromEntries(Object.entries(totals).map(([k, v]) => [k, Number(v.toFixed(2))])),
      grand_total: Number(lines.reduce((s, l) => s + l.amount, 0).toFixed(2)),
    };
  });

  const ndiaLines = (invNumMap = {}) => preview.lines.filter((l) => l.destination === "NDIA").map((l) => ({
    ndis_number: bill?.ndis_number || "", supports_from: l.shift_date,
    supports_to: l.shift_date,
    support_number: l.support_number, claim_reference: l.shift || "",
    invoice_number: invNumMap[l.participant] || "",
    hours: l.is_unit ? null : l.hours, quantity: l.is_unit ? l.quantity : null,
    unit_price: l.unit_price, gst_code: l.gst_code, claim_type: l.claim_type || "",
    cancellation_reason: l.cancellation_reason || "",
  }));

  const bulkNdiaLines = (invNumMap = {}) => incLines.filter((l) => l.destination === "NDIA").map((l) => ({
    ndis_number: l.ndis_number || "", supports_from: l.shift_date,
    supports_to: l.shift_date,
    support_number: l.support_number, claim_reference: l.shift || "",
    invoice_number: invNumMap[l.participant] || "",
    hours: l.is_unit ? null : l.hours, quantity: l.is_unit ? l.quantity : null,
    unit_price: l.unit_price, gst_code: l.gst_code, claim_type: l.claim_type || "",
    cancellation_reason: l.cancellation_reason || "",
  }));

  const downloadCsv = async (lines, filename) => {
    if (!lines.length) return toast.error("No NDIA-managed lines to export");
    setBusy(true);
    try {
      const res = await api.ndiaCsvBlob(lines);
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
      toast.success("NDIA CSV downloaded");
    } catch (e) {
      let msg = "CSV rejected";
      try { const t = JSON.parse(await e.response.data.text()); msg = (t.detail?.errors || []).slice(0, 3).join(" • ") || t.detail?.message || msg; } catch {}
      toast.error(msg);
    } finally { setBusy(false); }
  };

  const downloadNdia = () => downloadCsv(ndiaLines(), shortCsvName());
  const downloadBulkNdia = () => downloadCsv(bulkNdiaLines(), shortCsvName());

  const silPeriodsFrom = (lines) => lines.filter((l) => l.line_type === "sil_weekly").map((l) => ({
    participant: l.participant, sil_charge: l.sil_charge, week_start: l.sil_week_start,
    week_end: l.sil_week_end, amount: l.amount,
  }));

  const markInvoiced = async (invNumMap = {}) => {
    const shifts = [...new Set(preview.lines.map((l) => l.shift).filter(Boolean))];
    const silPeriods = silPeriodsFrom(preview.lines);
    if (!shifts.length && !silPeriods.length) return;
    setBusy(true);
    try {
      const ref = `INV-${participant}-${from}`;
      const res = await api.markInvoiced({
        shifts, sil_periods: silPeriods, reference: ref, run_type: "Single", date_from: from, date_to: to, check_id: singleCheck,
        totals: preview.totals, grand_total: preview.grand_total, participant_count: 1,
        ndia_lines: ndiaLines(invNumMap),
        participants: [{ name: preview.participant?.name || participant, display_name: preview.participant?.display_name || participant, management_type: preview.management_type, totals: preview.totals, total: preview.grand_total, shift_count: shifts.length }],
      });
      toast.success(`Invoiced ${shifts.length} shift(s)${silPeriods.length ? ` + ${silPeriods.length} SIL week(s)` : ""} (${ref}) — locked & removed from Approve tab`);
      loadHoldbacks(); runPreview(); await goToRun(res.run);
    } catch (e) { toast.error(e.response?.data?.detail || "Mark invoiced failed"); }
    finally { setBusy(false); }
  };

  const bulkMarkInvoiced = async (invNumMap = {}) => {
    const shifts = [...new Set(incLines.map((l) => l.shift).filter(Boolean))];
    const silPeriods = silPeriodsFrom(incLines);
    if (!shifts.length && !silPeriods.length) return toast.error("No participants included in this run");
    setBusy(true);
    try {
      const ref = `INV-BULK-${from}-${to}`;
      const res = await api.markInvoiced({
        shifts, sil_periods: silPeriods, reference: ref, run_type: "Bulk", date_from: from, date_to: to, check_id: bulkCheck,
        totals: incTotals, grand_total: incGrand,
        participant_count: incParts.length, participants: incParts,
        ndia_lines: bulkNdiaLines(invNumMap),
        excluded: bulk.participants.filter((p) => excluded.has(p.name)).map((p) => ({ name: p.name, display_name: p.display_name || p.name })),
      });
      toast.success(`${shifts.length} shift(s) across ${incParts.length} participant(s) locked & invoiced (${ref})`);
      loadHoldbacks(); runBulkPreview(); await goToRun(res.run);
    } catch (e) { toast.error(e.response?.data?.detail || "Bulk mark invoiced failed"); }
    finally { setBusy(false); }
  };

  // ---- Push DRAFT invoices to Xero (all destinations) ----
  const toPushLines = (lines, mgmtOf) => lines.map((l) => ({
    participant: l.participant, participant_name: l.participant_name, ndis_number: l.ndis_number || "",
    destination: l.destination, shift: l.shift || null, shift_date: l.shift_date, support_number: l.support_number,
    support_name: l.support_name, group: l.group, tier: l.tier, hours: l.hours, quantity: l.quantity,
    is_unit: !!l.is_unit, unit_price: l.unit_price, amount: l.amount, management_type: mgmtOf(l),
    from: l.from, to: l.to, shift_start: l.shift_start, shift_end: l.shift_end, multi_billing: !!l.multi_billing,
    worker_label: l.worker_label || null,
  }));

  const runPush = async (ref, lines, dfrom, dto, checkId) => {
    if (!xero.connected) { setShowXero(true); return toast.error("Connect to Xero first (Xero settings)") && null; }
    if (!xero.account_code_set) { setShowXero(true); return toast.error("Set the Xero revenue account code first") && null; }
    if (!lines.length) { toast.error("No lines to push"); return null; }
    if (!checkId) { toast.error("Run the invoice check and clear it before sending"); return null; }
    setBusy(true);
    try {
      const res = await api.xeroPush({ reference: ref, date_from: dfrom, date_to: dto, lines, check_id: checkId });
      const created = (res.invoices || []).filter((r) => r.status === "created").length;
      const skipped = (res.invoices || []).filter((r) => r.status === "skipped").length;
      if (res.errors?.length) {
        toast.error(`${created} pushed, ${res.errors.length} failed: ${res.errors.map((e) => e.detail).slice(0, 2).join(" • ")}`);
        if (created > 0) setPushed((p) => ({ ...p, [ref]: true }));
        return null;
      }
      if (created > 0) {
        toast.success(`${created} draft invoice(s) pushed to Xero${skipped ? ` · ${skipped} already existed` : ""}`);
        setPushed((p) => ({ ...p, [ref]: true }));
        return res.invoices;   // return full list so callers can extract invoice numbers
      }
      // Nothing new: every invoice already existed for this reference.
      toast.info(`All ${skipped} invoice(s) were already pushed to Xero for this reference — void the run in the history below to re-push.`);
      setPushed((p) => ({ ...p, [ref]: true }));
      return null;
    } catch (e) { toast.error(e.response?.data?.detail || "Xero push failed"); return null; }
    finally { setBusy(false); }
  };

  // Build a participant → Xero invoice number map from the push result (NDIA destination only).
  const buildInvNumMap = (invoices) =>
    Object.fromEntries(
      (invoices || [])
        .filter((r) => r.destination === "NDIA" && r.invoice_number)
        .map((r) => [r.participant, r.invoice_number])
    );

  const pushSingle = async () => {
    const invoices = await runPush(singleRef, toPushLines(preview.lines, () => preview.management_type), from, to, singleCheck);
    if (invoices) await markInvoiced(buildInvNumMap(invoices));
  };
  const pushBulk = async () => {
    const mgmt = Object.fromEntries((bulk?.participants || []).map((p) => [p.name, p.management_type]));
    const invoices = await runPush(bulkRef, toPushLines(incLines, (l) => mgmt[l.participant]), from, to, bulkCheck);
    if (invoices) await bulkMarkInvoiced(buildInvNumMap(invoices));
  };

  const addSplit = () => setBill((b) => ({ ...b, category_billing: [...(b.category_billing || []), { support_category: "", destination: "NDIA" }] }));
  const setSplit = (i, k, v) => setBill((b) => ({ ...b, category_billing: b.category_billing.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)) }));
  const rmSplit = (i) => setBill((b) => ({ ...b, category_billing: b.category_billing.filter((_, idx) => idx !== i) }));

  const isDual = bill?.management_type?.startsWith("Dual");
  const switchMode = (m) => { setMode(m); if (m === "setup" && !setupRows) loadSetup(); };

  // Bulk run respects excluded participants (held back for individual handling).
  const incLines = bulk ? bulk.lines.filter((l) => !excluded.has(l.participant)) : [];
  const incParts = bulk ? bulk.participants.filter((p) => !excluded.has(p.name)) : [];
  const incTotals = incLines.reduce((t, l) => { t[l.destination] = Number(((t[l.destination] || 0) + l.amount).toFixed(2)); return t; }, { NDIA: 0, "Plan Manager": 0, Self: 0 });
  const incGrand = Number(incLines.reduce((s, l) => s + l.amount, 0).toFixed(2));

  // ---- Bulk billing setup ----
  const [setupRows, setSetupRows] = useState(null);
  const [setupLoading, setSetupLoading] = useState(false);
  const [setupBusy, setSetupBusy] = useState(false);
  const [setupFilter, setSetupFilter] = useState("");
  const loadSetup = async () => {
    setSetupLoading(true);
    try { setSetupRows(await api.allParticipantBilling()); }
    catch { toast.error("Could not load participant billing"); }
    finally { setSetupLoading(false); }
  };
  const setSetupField = (name, field, value) =>
    setSetupRows((rows) => rows.map((r) => (r.name === name ? { ...r, [field]: value, _dirty: true } : r)));
  const saveSetup = async () => {
    const dirty = (setupRows || []).filter((r) => r._dirty);
    if (!dirty.length) return toast.info("No changes to save");
    setSetupBusy(true);
    try {
      await api.saveBulkParticipantBilling(dirty.map(({ _dirty, ...r }) => r));
      toast.success(`Billing setup saved for ${dirty.length} participant(s)`);
      setSetupRows((rows) => rows.map((r) => ({ ...r, _dirty: false })));
    } catch (e) { toast.error(e.response?.data?.detail || "Bulk save failed"); }
    finally { setSetupBusy(false); }
  };

  const downloadRunCsv = async (r) => {
    try {
      const res = await api.runNdiaCsvBlob(r.name);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = shortCsvName();
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.status === 404 ? "No NDIA CSV stored for this run" : "Could not download CSV");
    }
  };

  const clearRunXero = async (r) => {
    if (!window.confirm(`Allow re-push of ${r.reference}? This clears the Xero link so this batch can be pushed again. Delete the old draft invoices in Xero first to avoid duplicates.`)) return;
    try {
      const res = await api.clearRunXero(r.name);
      toast.success(`Xero link cleared for ${r.reference} (${res.cleared} record(s)) — re-preview the range and push again.`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not clear the Xero link"); }
  };

  const doVoidRun = async () => {
    if (!voidReason.trim()) return toast.error("A reason is required");
    setBusy(true);
    try {
      const res = await api.voidInvoiceRun(voidTarget.name, voidReason.trim());
      const sil = res.sil_released ? ` + ${res.sil_released} SIL week(s) released` : "";
      toast.success(`Batch ${voidTarget.reference} voided — ${res.reopened.length} shift(s) reopened to the Approve tab${sil}`);
      setVoidTarget(null); setVoidReason(""); loadRuns();
      if (mode === "bulk" && bulk) runBulkPreview();
    } catch (e) { toast.error(e.response?.data?.detail || "Void failed"); }
    finally { setBusy(false); }
  };

  const doVoidPart = async () => {
    if (!voidPartReason.trim()) return toast.error("A reason is required");
    setBusy(true);
    try {
      const res = await api.voidInvoiceParticipant(voidPart.run.name, voidPart.participant, voidPartReason.trim());
      const sil = res.sil_released ? ` + ${res.sil_released} SIL week(s) released` : "";
      toast.success(`Invoice for ${voidPart.display} voided — ${res.reopened.length} shift(s) reopened${sil}${res.run_voided ? " · whole run now voided" : ""}`);
      setVoidPart(null); setVoidPartReason(""); loadRuns();
      try { setExpandedRun(await api.invoiceRun(voidPart.run.name)); } catch { /* noop */ }
      if (mode === "bulk" && bulk) runBulkPreview();
    } catch (e) { toast.error(e.response?.data?.detail || "Void failed"); }
    finally { setBusy(false); }
  };

  const openRun = async (name) => {
    if (expandedRun?.name === name) { setExpandedRun(null); return; }
    try { setExpandedRun(await api.invoiceRun(name)); } catch { toast.error("Could not load run detail"); }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="invoicing-page">
      <div className="flex items-center gap-2 mb-1">
        <Receipt className="h-5 w-5 text-brand" />
        <h1 className="text-xl font-bold text-slate-900">Invoicing</h1>
        {xero.connected && (
          <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-[10px]" data-testid="xero-status-pill">
            Xero{xero.tenant_name ? ` · ${xero.tenant_name}` : ""}
          </Badge>
        )}
        {can.has("settings.accounts") && (
          <Button variant="outline" size="sm" onClick={() => setShowXero(true)} className="h-7 text-[11px] ml-auto" data-testid="open-xero-settings">
            <UploadCloud className="h-3.5 w-3.5 mr-1" /> Xero settings
          </Button>
        )}
      </div>
      <p className="text-xs text-slate-500 mb-4">Approved shifts are routed to NDIA (CSV export), Plan Manager or Self, then locked once invoiced.</p>

      <XeroSettings open={showXero} onOpenChange={setShowXero} onChanged={loadXero} />

      {/* Mode toggle */}
      <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5 mb-4" data-testid="invoicing-mode">
        <button onClick={() => switchMode("single")} data-testid="invoicing-mode-single"
          className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${mode === "single" ? "bg-brand text-white" : "text-slate-600 hover:bg-slate-50"}`}>
          <User className="h-3.5 w-3.5" /> Single participant
        </button>
        <button onClick={() => switchMode("bulk")} data-testid="invoicing-mode-bulk"
          className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${mode === "bulk" ? "bg-brand text-white" : "text-slate-600 hover:bg-slate-50"}`}>
          <Users className="h-3.5 w-3.5" /> Bulk (all participants)
        </button>
        <button onClick={() => switchMode("setup")} data-testid="invoicing-mode-setup"
          className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${mode === "setup" ? "bg-brand text-white" : "text-slate-600 hover:bg-slate-50"}`}>
          <Settings2 className="h-3.5 w-3.5" /> Billing setup
        </button>
      </div>

      {/* ============ BULK BILLING SETUP ============ */}
      {mode === "setup" && (
        <div data-testid="bulk-billing-setup">
          <div className="bg-white border border-slate-200 rounded-lg p-4 mb-4 flex flex-wrap items-center gap-3">
            <div className="text-xs text-slate-500 flex-1 min-w-[240px]">Set each participant's plan management (NDIA, Plan Managed, Self Managed or Dual) in bulk. Category splits for Dual participants are edited per-participant on the Single tab.</div>
            <Input placeholder="Filter participants…" value={setupFilter} onChange={(e) => setSetupFilter(e.target.value)} className="h-9 text-xs w-56" data-testid="setup-filter" />
            <Button variant="outline" onClick={loadSetup} disabled={setupLoading} className="h-9 text-xs" data-testid="setup-reload">
              {setupLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />}
            </Button>
            {can.has("invoicing.billing_setup") && <Button onClick={saveSetup} disabled={setupBusy} className="h-9 text-xs" style={{ backgroundColor: "var(--brand)" }} data-testid="setup-save-all">
              {setupBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save changes"}
            </Button>}
          </div>
          {setupLoading && !setupRows ? (
            <div className="text-center py-10 text-slate-400 text-sm"><Loader2 className="h-5 w-5 animate-spin inline mr-2" />Loading…</div>
          ) : (
            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 border-b text-slate-500 uppercase text-[10px]">
                  <tr>
                    <th className="text-left px-3 py-2">Participant</th>
                    <th className="text-left px-3 py-2">NDIS #</th>
                    <th className="text-left px-3 py-2 w-52">Plan management</th>
                    <th className="text-left px-3 py-2">Plan Manager / Bill-to</th>
                  </tr>
                </thead>
                <tbody>
                  {(setupRows || []).filter((r) => !setupFilter || (r.display_name || r.name).toLowerCase().includes(setupFilter.toLowerCase())).map((r) => {
                    const dual = (r.management_type || "").startsWith("Dual");
                    return (
                      <tr key={r.name} className={`border-b last:border-0 ${r._dirty ? "bg-amber-50" : ""}`} data-testid={`setup-row-${r.name}`}>
                        <td className="px-3 py-2 font-medium text-slate-800">{r.display_name || r.name}</td>
                        <td className="px-3 py-2 text-slate-500">{r.ndis_number || "—"}</td>
                        <td className="px-3 py-2">
                          <Select value={r.management_type || undefined} onValueChange={(v) => setSetupField(r.name, "management_type", v)}>
                            <SelectTrigger className="h-8 text-xs" data-testid={`setup-mgmt-${r.name}`}><SelectValue placeholder="Not set" /></SelectTrigger>
                            <SelectContent>{MGMT.map((m) => <SelectItem key={m} value={m} className="text-xs">{m}</SelectItem>)}</SelectContent>
                          </Select>
                        </td>
                        <td className="px-3 py-2">
                          {(r.management_type === "Plan Managed" || dual) && (
                            <div className="flex flex-wrap gap-1.5">
                              <Input placeholder="Plan Manager name" value={r.plan_manager_name || ""} onChange={(e) => setSetupField(r.name, "plan_manager_name", e.target.value)} className="h-8 text-xs w-40" />
                              <Input placeholder="Plan Manager email" value={r.plan_manager_email || ""} onChange={(e) => setSetupField(r.name, "plan_manager_email", e.target.value)} className="h-8 text-xs w-48" />
                            </div>
                          )}
                          {r.management_type === "Self Managed" && (
                            <div className="flex flex-wrap gap-1.5">
                              <Input placeholder="Bill-to name" value={r.self_bill_name || ""} onChange={(e) => setSetupField(r.name, "self_bill_name", e.target.value)} className="h-8 text-xs w-40" />
                              <Input placeholder="Bill-to email" value={r.self_bill_email || ""} onChange={(e) => setSetupField(r.name, "self_bill_email", e.target.value)} className="h-8 text-xs w-48" />
                            </div>
                          )}
                          {!r.management_type && <span className="text-amber-600">Not configured</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ============ SINGLE MODE ============ */}
      {mode === "single" && (
        <>
          {/* #2: participants held back from a bulk run, awaiting individual invoicing */}
          {holdbacks.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4" data-testid="holdback-panel">
              <div className="text-[11px] font-bold uppercase tracking-wider text-amber-700 mb-2 flex items-center gap-1.5">
                <FileWarning className="h-3.5 w-3.5" /> Held back from bulk runs ({holdbacks.length}) — invoice individually
              </div>
              <div className="flex flex-wrap gap-2">
                {holdbacks.map((hb) => (
                  <div key={hb.id} className="flex items-center gap-1 bg-white border border-amber-200 rounded-full pl-3 pr-1 py-1" data-testid={`holdback-${hb.participant}`}>
                    <button onClick={() => loadFromHoldback(hb)} className="text-xs font-medium text-slate-700 hover:text-brand" data-testid={`holdback-load-${hb.participant}`}>
                      {hb.participant_name} <span className="text-slate-400">· {hb.date_from} → {hb.date_to}</span>
                    </button>
                    {can.has("invoicing.run") && (
                      <button onClick={() => dismissHoldback(hb.id)} title="Remove from list" data-testid={`holdback-dismiss-${hb.participant}`}
                        className="h-5 w-5 rounded-full hover:bg-slate-100 text-slate-400 hover:text-rose-600 flex items-center justify-center">
                        <Trash2 className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          {/* Controls */}
          <div className="bg-white border border-slate-200 rounded-lg p-4 mb-4 flex flex-wrap items-end gap-3">
            <div className="min-w-[240px]">
              <Label className="text-[11px] font-semibold text-slate-500 uppercase">Participant</Label>
              <Combobox testid="invoicing-participant" options={pOpts} value={participant} onChange={loadBilling} placeholder="Select participant…" />
            </div>
            <div>
              <Label className="text-[11px] font-semibold text-slate-500 uppercase">Bill To</Label>
              <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-9 text-xs" data-testid="invoicing-to" />
            </div>
            <div className="flex flex-col gap-1 justify-end pb-0.5">
              <Label className="text-[11px] font-semibold text-slate-500 uppercase">Custom date range</Label>
              <div className="flex items-center gap-2 h-9">
                <Switch
                  checked={customRange}
                  onCheckedChange={setCustomRange}
                  data-testid="invoicing-custom-range-toggle"
                />
                <span className="text-[11px] text-slate-400">
                  {customRange ? "Manual from date" : `Auto 90-day lookback (from ${from})`}
                </span>
              </div>
            </div>
            {customRange && (
              <div>
                <Label className="text-[11px] font-semibold text-slate-500 uppercase">From</Label>
                <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-9 text-xs" data-testid="invoicing-from" />
              </div>
            )}
            <Button onClick={runPreview} disabled={loading || !participant} className="h-9 text-xs" data-testid="invoicing-preview-btn">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Preview invoice"}
            </Button>
            {bill && (
              <Button variant="outline" onClick={() => setShowSetup((s) => !s)} className="h-9 text-xs ml-auto" data-testid="invoicing-setup-toggle">
                <Settings2 className="h-3.5 w-3.5 mr-1" /> Billing setup
                {bill.management_type ? <Badge variant="outline" className="ml-2 text-[10px]">{bill.management_type}</Badge> : <Badge variant="outline" className="ml-2 text-[10px] bg-amber-50 text-amber-700">Not set</Badge>}
              </Button>
            )}
          </div>

          {/* Billing setup */}
          {bill && showSetup && (
            <div className="bg-white border border-rose-200 rounded-lg p-4 mb-4 space-y-3" data-testid="billing-setup">
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-[11px] font-semibold text-slate-500 uppercase">Plan Management</Label>
                  <Select value={bill.management_type || undefined} onValueChange={(v) => setBill((b) => ({ ...b, management_type: v }))}>
                    <SelectTrigger className="h-9 text-xs" data-testid="billing-mgmt-type"><SelectValue placeholder="Select…" /></SelectTrigger>
                    <SelectContent>{MGMT.map((m) => <SelectItem key={m} value={m} className="text-xs">{m}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="text-[11px] text-slate-500 self-end pb-1">NDIS #: <span className="font-semibold text-slate-700">{bill.ndis_number || "—"}</span></div>
              </div>
              {(bill.management_type === "Plan Managed" || isDual) && (
                <div className="grid sm:grid-cols-3 gap-3">
                  <div><Label className="text-[10px] text-slate-400">Plan Manager Name</Label><Input value={bill.plan_manager_name || ""} onChange={(e) => setBill((b) => ({ ...b, plan_manager_name: e.target.value }))} className="h-8 text-xs" /></div>
                  <div><Label className="text-[10px] text-slate-400">Plan Manager Email</Label><Input value={bill.plan_manager_email || ""} onChange={(e) => setBill((b) => ({ ...b, plan_manager_email: e.target.value }))} className="h-8 text-xs" /></div>
                  <div><Label className="text-[10px] text-slate-400">Plan Manager ABN</Label><Input value={bill.plan_manager_abn || ""} onChange={(e) => setBill((b) => ({ ...b, plan_manager_abn: e.target.value }))} className="h-8 text-xs" /></div>
                </div>
              )}
              {bill.management_type === "Self Managed" && (
                <div className="grid sm:grid-cols-2 gap-3">
                  <div><Label className="text-[10px] text-slate-400">Bill-To Name</Label><Input value={bill.self_bill_name || ""} onChange={(e) => setBill((b) => ({ ...b, self_bill_name: e.target.value }))} className="h-8 text-xs" /></div>
                  <div><Label className="text-[10px] text-slate-400">Bill-To Email</Label><Input value={bill.self_bill_email || ""} onChange={(e) => setBill((b) => ({ ...b, self_bill_email: e.target.value }))} className="h-8 text-xs" /></div>
                </div>
              )}
              {isDual && (
                <div>
                  <Label className="text-[11px] font-semibold text-slate-500 uppercase">Category split — which support categories go to the Plan Manager vs NDIA</Label>
                  <div className="space-y-2 mt-1">
                    {(bill.category_billing || []).map((r, i) => (
                      <div key={i} className="flex gap-2 items-center" data-testid={`split-row-${i}`}>
                        <Select value={r.support_category || undefined} onValueChange={(v) => setSplit(i, "support_category", v)}>
                          <SelectTrigger className="h-8 text-xs flex-1"><SelectValue placeholder="Support category…" /></SelectTrigger>
                          <SelectContent className="max-h-64">{groupOpts.map((g) => <SelectItem key={g} value={g} className="text-xs">{g}</SelectItem>)}</SelectContent>
                        </Select>
                        <Select value={r.destination} onValueChange={(v) => setSplit(i, "destination", v)}>
                          <SelectTrigger className="h-8 text-xs w-36"><SelectValue /></SelectTrigger>
                          <SelectContent><SelectItem value="NDIA" className="text-xs">NDIA</SelectItem><SelectItem value="Plan Manager" className="text-xs">Plan Manager</SelectItem></SelectContent>
                        </Select>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500" onClick={() => rmSplit(i)}><Trash2 className="h-3.5 w-3.5" /></Button>
                      </div>
                    ))}
                    <Button variant="outline" size="sm" onClick={addSplit} className="h-7 text-xs"><Plus className="h-3 w-3 mr-1" />Add category</Button>
                    <p className="text-[10px] text-slate-400">Unlisted categories default to NDIA. You can still override any line below at generation time.</p>
                  </div>
                </div>
              )}
              <div className="grid gap-3 sm:grid-cols-2" data-testid="billing-multi-worker">
                <div>
                  <Label className="text-[10px] text-slate-400">2:1 support on invoices</Label>
                  <Select value={bill.two_to_one_lines || "separate"} onValueChange={(v) => setBill((b) => ({ ...b, two_to_one_lines: v }))}>
                    <SelectTrigger className="h-8 text-xs" data-testid="billing-two-to-one"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="separate" className="text-xs">One line per worker (default)</SelectItem>
                      <SelectItem value="combined" className="text-xs">One combined line (hours of every worker)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-[10px] text-slate-400 mt-1">For shifts billed for each worker. Switch to one combined line if this participant's plan manager or the NDIA rejects the separate lines as duplicates.</p>
                </div>
                <label className="flex items-start gap-2 text-xs text-slate-700 mt-4 cursor-pointer">
                  <input type="checkbox" className="accent-brand mt-0.5" checked={!!bill.complex_support_needs}
                    onChange={(e) => setBill((b) => ({ ...b, complex_support_needs: e.target.checked }))} data-testid="billing-complex-needs" />
                  <span>Complex support needs<span className="block text-[10px] text-slate-400">Shadow shifts bill both workers (NDIS: up to 6 weekday hours a year). For example very limited communication, behaviour support, or medical procedures such as ventilation or tube feeding.</span></span>
                </label>
              </div>
              {can.has("invoicing.billing_setup") && <Button onClick={saveBilling} disabled={busy} className="h-8 text-xs" data-testid="billing-save">Save billing setup</Button>}
            </div>
          )}

          {/* Warnings */}
          {preview?.warnings?.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 text-xs text-amber-800" data-testid="invoicing-warnings">
              <div className="font-semibold flex items-center gap-1 mb-1"><FileWarning className="h-3.5 w-3.5" /> {preview.warnings.length} shift(s) skipped or need attention</div>
              <ul className="list-disc ml-5 space-y-0.5">{preview.warnings.slice(0, 6).map((w, i) => <li key={i}>{w}</li>)}</ul>
            </div>
          )}

          {/* Preview */}
          {preview && preview.lines.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="invoicing-preview">
              <div className="flex flex-wrap items-center gap-3 px-4 py-3 border-b bg-slate-50">
                {["NDIA", "Plan Manager", "Self"].map((d) => (
                  <Badge key={d} variant="outline" className={`text-[11px] ${DEST_COLOUR[d]}`}>{d}: {money(preview.totals[d])}</Badge>
                ))}
                <span className="text-xs font-bold text-slate-700 ml-auto">Total: {money(preview.grand_total)}</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 text-slate-500">
                    <tr className="text-left">
                      <th className="px-3 py-2 font-semibold">Date</th>
                      <th className="px-3 py-2 font-semibold">Support #</th>
                      <th className="px-3 py-2 font-semibold">Description</th>
                      <th className="px-3 py-2 font-semibold text-right">Hrs/Qty</th>
                      <th className="px-3 py-2 font-semibold text-right">Unit</th>
                      <th className="px-3 py-2 font-semibold text-right">Amount</th>
                      <th className="px-3 py-2 font-semibold">Destination</th>
                      <th className="px-3 py-2 font-semibold">GST</th>
                      <th className="px-3 py-2 font-semibold">Claim</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.lines.map((l, i) => (
                      <tr key={i} className="border-t border-slate-100" data-testid={`invoice-line-${i}`}>
                        <td className="px-3 py-1.5 whitespace-nowrap">{l.shift_date}</td>
                        <td className="px-3 py-1.5 font-mono text-[10px]">{l.support_number}</td>
                        <td className="px-3 py-1.5 max-w-[240px]">
                          {can.has("invoicing.run")
                            ? <Input value={l.support_name || ""} onChange={(e) => setLine(i, "support_name", e.target.value)} className="h-7 text-[11px] w-full min-w-[180px]" data-testid={`line-desc-${i}`} />
                            : <span className="truncate block" title={l.support_name}>{l.support_name}</span>}
                          {l.worker_label && <div className="text-[10px] text-violet-600 font-semibold mt-0.5" data-testid={`line-worker-${i}`}>{l.worker_label}</div>}
                        </td>
                        <td className="px-3 py-1.5 text-right">
                          {can.has("invoicing.run") ? (
                            <div className="flex items-center justify-end gap-1">
                              <Input type="number" step={l.is_unit ? "1" : "0.25"} min="0" value={l.is_unit ? l.quantity : l.hours}
                                onChange={(e) => setLineNum(i, l.is_unit ? "quantity" : "hours", e.target.value)}
                                className="h-7 w-16 text-[11px] text-right" data-testid={`line-qty-${i}`} />
                              <span className="text-[10px] text-slate-400">{l.is_unit ? "u" : "h"}</span>
                            </div>
                          ) : (l.is_unit ? `${l.quantity} u` : `${l.hours} h`)}
                        </td>
                        <td className="px-3 py-1.5 text-right">
                          {can.has("invoicing.run")
                            ? <Input type="number" step="0.01" min="0" value={l.unit_price} onChange={(e) => setLineNum(i, "unit_price", e.target.value)} className="h-7 w-20 text-[11px] text-right" data-testid={`line-unit-${i}`} />
                            : money(l.unit_price)}
                        </td>
                        <td className="px-3 py-1.5 text-right font-semibold" data-testid={`line-amount-${i}`}>{money(l.amount)}</td>
                        <td className="px-3 py-1.5">
                          <Select value={l.destination} onValueChange={(v) => setLine(i, "destination", v)}>
                            <SelectTrigger className="h-7 text-[11px] w-32" data-testid={`line-dest-${i}`}><SelectValue /></SelectTrigger>
                            <SelectContent><SelectItem value="NDIA" className="text-xs">NDIA</SelectItem><SelectItem value="Plan Manager" className="text-xs">Plan Manager</SelectItem><SelectItem value="Self" className="text-xs">Self</SelectItem></SelectContent>
                          </Select>
                        </td>
                        <td className="px-3 py-1.5">
                          <Select value={l.gst_code} onValueChange={(v) => setLine(i, "gst_code", v)}>
                            <SelectTrigger className="h-7 text-[11px] w-20" data-testid={`line-gst-${i}`}><SelectValue /></SelectTrigger>
                            <SelectContent><SelectItem value="P2" className="text-xs">P2 (Free)</SelectItem><SelectItem value="P1" className="text-xs">P1 (+GST)</SelectItem><SelectItem value="P5" className="text-xs">P5</SelectItem></SelectContent>
                          </Select>
                        </td>
                        <td className="px-3 py-1.5">
                          <Select value={l.claim_type || "STD"} onValueChange={(v) => setLine(i, "claim_type", v === "STD" ? "" : v)}>
                            <SelectTrigger className="h-7 text-[11px] w-24" data-testid={`line-claim-${i}`}><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="STD" className="text-xs">Standard</SelectItem>
                              <SelectItem value="TRAN" className="text-xs">TRAN</SelectItem>
                              <SelectItem value="NF2F" className="text-xs">NF2F</SelectItem>
                              <SelectItem value="REPW" className="text-xs">REPW</SelectItem>
                              <SelectItem value="CANC" className="text-xs">CANC</SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {can.has("invoicing.check", "invoicing.run") && (
                <InvoiceCheckPanel key={linesKey(preview.lines)} lines={preview.lines} reference={singleRef}
                  dateFrom={from} dateTo={to} onReady={setSingleCheck} />
              )}
              <div className="flex flex-wrap items-center gap-2 px-4 py-3 border-t bg-slate-50">
                {can.has("invoicing.run") && (
                  <Button onClick={pushSingle} disabled={busy || !preview.lines.length || !singleCheck} className="h-9 text-xs" style={{ backgroundColor: "#13b5ea" }} data-testid="push-xero">
                    <UploadCloud className="h-3.5 w-3.5 mr-1" /> {pushed[singleRef] ? "Re-push to Xero & lock" : `Push to Xero & lock (${money(preview.grand_total)})`}
                  </Button>
                )}
                <Button onClick={downloadNdia} disabled={busy || preview.totals.NDIA === 0 || (xero.connected && !pushed[singleRef])} variant="outline" className="h-9 text-xs" data-testid="download-ndia-csv">
                  <Download className="h-3.5 w-3.5 mr-1" /> Download NDIA CSV ({money(preview.totals.NDIA)})
                </Button>
                {xero.connected && !pushed[singleRef] && preview.totals.NDIA > 0 && (
                  <span className="text-[11px] text-amber-600">Pushing to Xero also locks this invoice &amp; unlocks the NDIA CSV</span>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {/* ============ BULK MODE ============ */}
      {mode === "bulk" && (
        <>
          <div className="bg-white border border-slate-200 rounded-lg p-4 mb-4 flex flex-wrap items-end gap-3" data-testid="bulk-controls">
            <div>
              <Label className="text-[11px] font-semibold text-slate-500 uppercase">Bill To</Label>
              <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-9 text-xs" data-testid="bulk-to" />
            </div>
            <div className="flex flex-col gap-1 justify-end pb-0.5">
              <Label className="text-[11px] font-semibold text-slate-500 uppercase">Custom date range</Label>
              <div className="flex items-center gap-2 h-9">
                <Switch
                  checked={customRange}
                  onCheckedChange={setCustomRange}
                  data-testid="bulk-custom-range-toggle"
                />
                <span className="text-[11px] text-slate-400">
                  {customRange ? "Manual from date" : `Auto 90-day lookback (from ${from})`}
                </span>
              </div>
            </div>
            {customRange && (
              <div>
                <Label className="text-[11px] font-semibold text-slate-500 uppercase">From</Label>
                <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-9 text-xs" data-testid="bulk-from" />
              </div>
            )}
            <Button onClick={runBulkPreview} disabled={bulkLoading} className="h-9 text-xs" data-testid="bulk-preview-btn">
              {bulkLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Preview all participants"}
            </Button>
            <span className="text-[11px] text-slate-400 ml-1">Previews every participant with approved, un-invoiced shifts in the range.</span>
          </div>

          {/* Bulk warnings */}
          {bulk?.warnings?.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 text-xs text-amber-800" data-testid="bulk-warnings">
              <div className="font-semibold flex items-center gap-1 mb-1"><FileWarning className="h-3.5 w-3.5" /> {bulk.warnings.length} shift(s) skipped or need attention</div>
              <ul className="list-disc ml-5 space-y-0.5">{bulk.warnings.slice(0, 8).map((w, i) => <li key={i}>{w}</li>)}</ul>
            </div>
          )}

          {bulk && bulk.participants.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="bulk-preview">
              {/* Summary bar */}
              <div className="flex flex-wrap items-center gap-3 px-4 py-3 border-b bg-slate-50">
                <Badge variant="outline" className="text-[11px] bg-slate-100 text-slate-700">{incParts.length} of {bulk.participant_count} included</Badge>
                {excluded.size > 0 && <Badge variant="outline" className="text-[11px] bg-rose-50 text-rose-700 border-rose-200" data-testid="bulk-excluded-count">{excluded.size} excluded</Badge>}
                {["NDIA", "Plan Manager", "Self"].map((d) => (
                  <Badge key={d} variant="outline" className={`text-[11px] ${DEST_COLOUR[d]}`}>{d}: {money(incTotals[d])}</Badge>
                ))}
                <span className="text-xs font-bold text-slate-700 ml-auto">Run total: {money(incGrand)}</span>
              </div>

              {/* Per-participant summary */}
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 text-slate-500">
                    <tr className="text-left">
                      <th className="px-3 py-2 font-semibold w-16 text-center">In run</th>
                      <th className="px-3 py-2 font-semibold">Participant</th>
                      <th className="px-3 py-2 font-semibold">Management</th>
                      <th className="px-3 py-2 font-semibold text-right">Shifts</th>
                      <th className="px-3 py-2 font-semibold text-right">NDIA</th>
                      <th className="px-3 py-2 font-semibold text-right">Plan Mgr</th>
                      <th className="px-3 py-2 font-semibold text-right">Self</th>
                      <th className="px-3 py-2 font-semibold text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bulk.participants.map((p) => {
                      const ex = excluded.has(p.name);
                      return (
                      <tr key={p.name} className={`border-t border-slate-100 ${ex ? "opacity-45 bg-slate-50" : ""}`} data-testid={`bulk-part-${p.name}`}>
                        <td className="px-3 py-1.5 text-center">
                          <input type="checkbox" checked={!ex} onChange={() => toggleExclude(p.name)}
                            className="h-4 w-4 accent-brand cursor-pointer" title={ex ? "Excluded — click to include" : "Included — click to exclude (handle individually)"}
                            data-testid={`bulk-include-${p.name}`} />
                        </td>
                        <td className="px-3 py-1.5">
                          <span className="font-medium text-slate-800">{p.display_name || p.name}</span>
                          {ex && <Badge variant="outline" className="ml-2 text-[9px] bg-rose-50 text-rose-700 border-rose-200">Excluded</Badge>}
                          {p.missing_config && <Badge variant="outline" className="ml-2 text-[9px] bg-amber-50 text-amber-700 border-amber-200" data-testid={`bulk-missing-${p.name}`}>No billing config → NDIA</Badge>}
                        </td>
                        <td className="px-3 py-1.5 text-slate-500">{p.management_type}</td>
                        <td className="px-3 py-1.5 text-right">{p.shift_count}</td>
                        <td className="px-3 py-1.5 text-right">{money(p.totals.NDIA)}</td>
                        <td className="px-3 py-1.5 text-right">{money(p.totals["Plan Manager"])}</td>
                        <td className="px-3 py-1.5 text-right">{money(p.totals.Self)}</td>
                        <td className="px-3 py-1.5 text-right font-semibold">{money(p.total)}</td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Line detail toggle */}
              <div className="border-t">
                <button onClick={() => setShowLines((s) => !s)} className="w-full flex items-center gap-1.5 px-4 py-2 text-[11px] font-semibold text-slate-500 hover:bg-slate-50" data-testid="bulk-lines-toggle">
                  {showLines ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                  {showLines ? "Hide" : "Show"} all {bulk.lines.length} line(s)
                </button>
                {showLines && (
                  <div className="overflow-x-auto border-t" data-testid="bulk-lines">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-50 text-slate-500">
                        <tr className="text-left">
                          <th className="px-3 py-2 font-semibold">Participant</th>
                          <th className="px-3 py-2 font-semibold">Date</th>
                          <th className="px-3 py-2 font-semibold">Support #</th>
                          <th className="px-3 py-2 font-semibold">Description</th>
                          <th className="px-3 py-2 font-semibold text-right">Hrs/Qty</th>
                          <th className="px-3 py-2 font-semibold text-right">Unit</th>
                          <th className="px-3 py-2 font-semibold text-right">Amount</th>
                          <th className="px-3 py-2 font-semibold">Dest</th>
                        </tr>
                      </thead>
                      <tbody>
                        {bulk.lines.map((l, i) => (
                          <tr key={i} className={`border-t border-slate-100 ${excluded.has(l.participant) ? "opacity-40 line-through" : ""}`}>
                            <td className="px-3 py-1.5 whitespace-nowrap">{l.participant_name}</td>
                            <td className="px-3 py-1.5 whitespace-nowrap">{l.shift_date}</td>
                            <td className="px-3 py-1.5 font-mono text-[10px]">{l.support_number}</td>
                            <td className="px-3 py-1.5 max-w-[200px]"><div className="truncate" title={l.support_name}>{l.support_name}</div>
                              {l.worker_label && <div className="text-[10px] text-violet-600 font-semibold truncate" title={l.worker_label}>{l.worker_label}</div>}</td>
                            <td className="px-3 py-1.5 text-right">{l.is_unit ? `${l.quantity} u` : `${l.hours} h`}</td>
                            <td className="px-3 py-1.5 text-right">{money(l.unit_price)}</td>
                            <td className="px-3 py-1.5 text-right font-semibold">{money(l.amount)}</td>
                            <td className="px-3 py-1.5"><Badge variant="outline" className={`text-[10px] ${DEST_COLOUR[l.destination]}`}>{l.destination}</Badge></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {can.has("invoicing.check", "invoicing.run") && (
                <InvoiceCheckPanel key={linesKey(bulk.lines)} lines={bulk.lines} reference={bulkRef}
                  dateFrom={from} dateTo={to} excluded={excluded} onReady={setBulkCheck} testid="bulk-invoice-check" />
              )}

              {/* Actions */}
              <div className="flex flex-wrap items-center gap-2 px-4 py-3 border-t bg-slate-50">
                {can.has("invoicing.run") && (
                  <Button onClick={pushBulk} disabled={busy || !incLines.length || !bulkCheck} className="h-9 text-xs" style={{ backgroundColor: "#13b5ea" }} data-testid="bulk-push-xero">
                    <UploadCloud className="h-3.5 w-3.5 mr-1" /> {pushed[bulkRef] ? "Re-push to Xero & lock" : `Push ${incParts.length} to Xero & lock (${money(incGrand)})`}
                  </Button>
                )}
                <Button onClick={downloadBulkNdia} disabled={busy || incTotals.NDIA === 0 || (xero.connected && !pushed[bulkRef])} variant="outline" className="h-9 text-xs" data-testid="bulk-download-ndia">
                  <Download className="h-3.5 w-3.5 mr-1" /> Download combined NDIA CSV ({money(incTotals.NDIA)})
                </Button>
                {xero.connected && !pushed[bulkRef] && incTotals.NDIA > 0 && (
                  <span className="text-[11px] text-amber-600">Pushing to Xero also locks the included invoices &amp; unlocks the NDIA CSV</span>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {/* ============ INVOICE RUN HISTORY ============ */}
      <div className="mt-6 bg-white border border-slate-200 rounded-lg" data-testid="invoice-runs">
        <button onClick={() => setRunsOpen((s) => !s)} className="w-full flex items-center gap-2 px-4 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50" data-testid="invoice-runs-toggle">
          <History className="h-4 w-4 text-slate-500" />
          Invoice run history
          <Badge variant="outline" className="text-[10px]">{runs.length}</Badge>
          <span className="ml-auto">{runsOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}</span>
        </button>
        {runsOpen && (
          <div className="border-t">
            {runs.length === 0 ? (
              <p className="px-4 py-6 text-xs text-slate-400 text-center">No billing runs recorded yet. Lock an invoice to create one.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="invoice-runs-list">
                  <thead className="bg-slate-50 text-slate-500">
                    <tr className="text-left">
                      <th className="px-3 py-2 font-semibold">Reference</th>
                      <th className="px-3 py-2 font-semibold">Type</th>
                      <th className="px-3 py-2 font-semibold">Range</th>
                      <th className="px-3 py-2 font-semibold text-right">Parts</th>
                      <th className="px-3 py-2 font-semibold text-right">Shifts</th>
                      <th className="px-3 py-2 font-semibold text-right">Total</th>
                      <th className="px-3 py-2 font-semibold">Status</th>
                      <th className="px-3 py-2 font-semibold">When</th>
                      <th className="px-3 py-2 font-semibold"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {runs.map((r) => (
                      <Fragment key={r.name}>
                        <tr className="border-t border-slate-100" data-testid={`run-${r.name}`}>
                          <td className="px-3 py-1.5">
                            <button onClick={() => openRun(r.name)} className="font-mono text-[10px] text-brand hover:underline flex items-center gap-1" data-testid={`run-open-${r.name}`}>
                              {expandedRun?.name === r.name ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                              {r.reference}
                            </button>
                          </td>
                          <td className="px-3 py-1.5">{r.run_type}</td>
                          <td className="px-3 py-1.5 whitespace-nowrap">{r.date_from} → {r.date_to}</td>
                          <td className="px-3 py-1.5 text-right">{r.participant_count}</td>
                          <td className="px-3 py-1.5 text-right">{r.shift_count}</td>
                          <td className="px-3 py-1.5 text-right font-semibold">{money(r.grand_total)}</td>
                          <td className="px-3 py-1.5">
                            <Badge variant="outline" className={`text-[10px] ${r.status === "Voided" ? "bg-slate-100 text-slate-500 border-slate-200" : "bg-teal-50 text-teal-700 border-teal-200"}`}>{r.status}</Badge>
                          </td>
                          <td className="px-3 py-1.5 text-slate-400 whitespace-nowrap">{fmtLog(r.created_at)}</td>
                          <td className="px-3 py-1.5 text-right">
                            {r.total_ndia > 0 && (
                              <Button variant="ghost" size="sm" className="h-7 text-[11px] text-slate-600" onClick={() => downloadRunCsv(r)} data-testid={`run-csv-${r.name}`}>
                                <Download className="h-3.5 w-3.5 mr-1" /> NDIA CSV
                              </Button>
                            )}
                            {can.has("invoicing.void") && r.status === "Locked" && (
                              <Button variant="ghost" size="sm" className="h-7 text-[11px] text-red-600" onClick={() => { setVoidTarget(r); setVoidReason(""); }} data-testid={`run-void-${r.name}`}>
                                <RotateCcw className="h-3.5 w-3.5 mr-1" /> Void / reopen
                              </Button>
                            )}
                            {can.has("invoicing.run") && r.status === "Voided" && (
                              <Button variant="ghost" size="sm" className="h-7 text-[11px] text-sky-600" onClick={() => clearRunXero(r)} data-testid={`run-clearxero-${r.name}`}>
                                <UploadCloud className="h-3.5 w-3.5 mr-1" /> Allow re-push
                              </Button>
                            )}
                          </td>
                        </tr>
                        {expandedRun?.name === r.name && (
                          <tr className="bg-slate-50/60" data-testid={`run-detail-${r.name}`}>
                            <td colSpan={9} className="px-4 py-3">
                              <div className="flex flex-wrap gap-2 mb-2">
                                {["NDIA", "Plan Manager", "Self"].map((d) => (
                                  <Badge key={d} variant="outline" className={`text-[10px] ${DEST_COLOUR[d]}`}>{d}: {money(d === "NDIA" ? expandedRun.total_ndia : d === "Plan Manager" ? expandedRun.total_plan_manager : expandedRun.total_self)}</Badge>
                                ))}
                                {expandedRun.status === "Voided" && <span className="text-[10px] text-slate-500 ml-2">Voided by {expandedRun.voided_by_user} · {fmtLog(expandedRun.voided_at)} — “{expandedRun.void_reason}”</span>}
                              </div>
                              {(expandedRun.participants_json || []).length > 0 ? (
                                <div className="text-[11px] text-slate-600 space-y-0.5">
                                  {expandedRun.participants_json.map((p, i) => (
                                    <div key={i} className="flex items-center gap-3" data-testid={`run-participant-${i}`}>
                                      <span className={`font-medium min-w-[160px] ${p.voided ? "line-through text-slate-400" : ""}`}>{p.display_name || p.name}</span>
                                      <span className="text-slate-400">{p.shift_count} shift(s)</span>
                                      <span className={`ml-auto font-semibold ${p.voided ? "line-through text-slate-400" : ""}`}>{money(p.total)}</span>
                                      {p.voided ? (
                                        <span className="text-[10px] text-slate-400 min-w-[70px] text-right" title={`Voided by ${p.voided_by || ""} — ${p.void_reason || ""}`}>Voided</span>
                                      ) : (can.has("invoicing.void") && r.status !== "Voided") ? (
                                        <Button variant="ghost" size="sm" className="h-6 px-1.5 text-[10px] text-red-600 min-w-[70px]" onClick={() => { setVoidPart({ run: r, participant: p.name, display: p.display_name || p.name }); setVoidPartReason(""); }} data-testid={`void-participant-${p.name}`}>
                                          <RotateCcw className="h-3 w-3 mr-1" /> Void
                                        </Button>
                                      ) : <span className="min-w-[70px]" />}
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <p className="text-[11px] text-slate-400">{expandedRun.shift_ids?.length || 0} shift(s) in this batch.</p>
                              )}
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Void run dialog */}
      <Dialog open={!!voidTarget} onOpenChange={(o) => { if (!o) { setVoidTarget(null); setVoidReason(""); } }}>
        <DialogContent data-testid="void-run-dialog">
          <DialogHeader><DialogTitle>Void &amp; reopen batch {voidTarget?.reference}</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500">This unlocks all {voidTarget?.shift_count} shift(s) in the batch — they return to the Approve tab and can be edited or re-invoiced. Give a reason (kept on the audit ledger).</p>
          <p className="text-[11px] text-amber-600">Note: any draft invoices already pushed to Xero are <span className="font-semibold">not</span> removed automatically — delete or void them in Xero before re-pushing to avoid duplicates.</p>
          <Textarea value={voidReason} onChange={(e) => setVoidReason(e.target.value)} placeholder="e.g. Wrong date range, corrected billing config…" className="text-xs" rows={3} data-testid="void-run-reason" />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setVoidTarget(null); setVoidReason(""); }} className="h-8 text-xs">Cancel</Button>
            <Button onClick={doVoidRun} disabled={busy || !voidReason.trim()} className="h-8 text-xs bg-red-600 hover:bg-red-700" data-testid="void-run-confirm">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Void & reopen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Void single-participant invoice dialog */}
      <Dialog open={!!voidPart} onOpenChange={(o) => { if (!o) { setVoidPart(null); setVoidPartReason(""); } }}>
        <DialogContent data-testid="void-participant-dialog">
          <DialogHeader><DialogTitle>Void invoice — {voidPart?.display}</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500">This voids just this participant's invoice in batch {voidPart?.run?.reference}. Their shifts return to the Approve tab (editable / re-invoiceable) and any SIL weeks are released. Other participants in the batch are unaffected. Give a reason (kept on the audit ledger).</p>
          <p className="text-[11px] text-amber-600">Note: a draft invoice already pushed to Xero for this participant is <span className="font-semibold">not</span> removed automatically — delete or void it in Xero before re-pushing.</p>
          <Textarea value={voidPartReason} onChange={(e) => setVoidPartReason(e.target.value)} placeholder="e.g. Wrong shift included, corrected billing…" className="text-xs" rows={3} data-testid="void-participant-reason" />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setVoidPart(null); setVoidPartReason(""); }} className="h-8 text-xs">Cancel</Button>
            <Button onClick={doVoidPart} disabled={busy || !voidPartReason.trim()} className="h-8 text-xs bg-red-600 hover:bg-red-700" data-testid="void-participant-confirm">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Void this invoice"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
